import {
   html,
   LitElement,
   nothing,
   PropertyDeclarations,
   PropertyValues,
} from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import { getGlobalStyleSheets } from '../../global-styles';

import * as Tags from './user-menus.tagnames';
import { IconEnums } from '../icon';
import { BootstrapDropdown } from '../BootstrapDrowndown';
import {
   AtlUserNotificationsMenuToggleEvent,
   AtlUserProfileMenuItemClickEvent,
   AtlUserProfileMenuToggleEvent,
} from './user-menus.events';
import { ifDefined } from 'lit/directives/if-defined.js';
import { AtlIconTemplate } from '../icon/icon.component';

@customElement(Tags.UserProfileMenu)
export class AtlUserProfileMenu extends LitElement {
   declare profilePicture?: string;
   declare profilePictureColor?: string;
   declare displayName?: string;
   declare initials?: string;
   declare email?: string;
   declare secondaryDetails?: string;

   private _bootStrapDropdown?: BootstrapDropdown;

   @query('button.btn')
   private _toggleButton?: HTMLButtonElement;

   @query('.dropdown-menu')
   _dropDownList?: HTMLUListElement;

   static styles = [
      ...getGlobalStyleSheets(),
   ];

   static properties: PropertyDeclarations = {
      profilePicture: { attribute: 'profile-picture', type: String },
      profilePictureColor: { attribute: 'profile-picture-color', type: String },
      displayName: { attribute: 'display-name', type: String },
      initials: { type: String },
      email: { type: String },
      secondaryDetails: { attribute: 'secondary-details', type: String },
   };

   constructor() {
      super();
      this.addEventListener(
         AtlUserProfileMenuItemClickEvent.EVENT_NAME,
         this._panelHideHandler
      );
   }

   override connectedCallback(): void {
      super.connectedCallback();
      window.addEventListener('click', this._handleWindowClick);
      window.addEventListener(
         AtlUserNotificationsMenuToggleEvent.EVENT_NAME,
         this._panelHideHandler
      );
   }

   override disconnectedCallback(): void {
      window.removeEventListener(
         AtlUserNotificationsMenuToggleEvent.EVENT_NAME,
         this._panelHideHandler
      );
      window.removeEventListener('click', this._handleWindowClick);
      super.disconnectedCallback();
   }

   private _onSlotChange = () => {
      const slot = this.shadowRoot?.querySelector('slot');

      const assignedNodes = (slot as HTMLSlotElement)?.assignedNodes({
         flatten: false,
      });

      const elements = assignedNodes?.filter(
         (node): node is HTMLElement =>
            node.nodeType === Node.ELEMENT_NODE &&
            (node as HTMLElement).tagName?.toLowerCase() !== Tags.UserProfileMenuDivider
      );

      elements?.forEach((item) => {
         item.classList.add('dropdown-item', 'p-2');
         item.setAttribute('data-type', 'user-profile-menu-item');
         item.setAttribute('role', 'listitem');
      });
   };

   private _renderButtonContent = () => {
      if (!this.profilePicture) {
         return AtlIconTemplate({
            iconSource: IconEnums.IconSource.FontAwesome,
            iconName: 'user',
            iconClassNames: 'menu-icon',
            ariaHidden: true,
         });
      }

      return html`<img src="${this.profilePicture}" class="menu-icon" alt="user profile picture"></img>`;
   };

   private _panelHideHandler = () => {
      this._bootStrapDropdown?.hide();
   };

   protected override firstUpdated(changedProperties: PropertyValues): void {
      super.firstUpdated(changedProperties);

      this._bootStrapDropdown = bootstrap.Dropdown.getOrCreateInstance(
         this._toggleButton
      );
   }

   private _onToggle = () => {
      this._bootStrapDropdown?.toggle();
      this.dispatchEvent(new AtlUserProfileMenuToggleEvent());
   };

   private _handleWindowClick = (event: MouseEvent) => {
      if (!this._dropDownList || !this._dropDownList.classList.contains('show'))
         return;

      const path = event.composedPath();

      const isButtonClicked =
         this._toggleButton && path.includes(this._toggleButton);
      if (isButtonClicked) return;

      const isClickInside =
         this._dropDownList && path.includes(this._dropDownList);
      if (!isClickInside) {
         this._bootStrapDropdown?.hide();
      }
   };

   render() {
      return html`
         <div class="dropdown user-profile-menu">
            <button
               class="btn dropdown-toggle"
               type="button"
               aria-label="user profile menu button"
               data-type="user-profile-menu-toggle"
               data-flip="false"
               data-bs-toggle="dropdown"
               data-bs-auto-close="outside"
               data-bs-display="static"
               @click=${this._onToggle}
            >
               ${this._renderButtonContent()}
            </button>
            <div class="dropdown-menu dropdown-menu-end py-0" role="list" tabindex="0">
               <div class="status-title-dropdown" role="presentation">
                  <div class="account-picture">
                     <span part="initials" style=${this.profilePictureColor ? styleMap({ 'background-color': this.profilePictureColor }) : nothing}>${this.initials}</span>
                  </div>
                  <div class="account-details">
                     <span
                        class="primary-details"
                        title="${ifDefined(this.displayName)}"
                        >${this.displayName}</span
                     >
                     ${this.email
                        ? html`<span
                             class="secondary-details"
                             title="${this.email}"
                             >${this.email}</span
                          >`
                        : nothing}
                     ${this.secondaryDetails
                        ? html`<span
                             class="secondary-details"
                             title="${this.secondaryDetails}"
                             >${this.secondaryDetails}</span
                          >`
                        : nothing}
                  </div>
               </div>
               <slot @slotchange=${this._onSlotChange}></slot>
            </div>
         </div>
      `;
   }
}
